package org.teddapp.project.ui.finance

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.input.VisualTransformation
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import com.tedd.db.Finance_account
import com.tedd.db.Loan
import com.tedd.db.Loan_payment
import org.teddapp.project.db.TODAY_ISO
import org.teddapp.project.db.repo.LoanDirection
import org.teddapp.project.db.repo.LoanMath
import org.teddapp.project.ui.TeddButton
import org.teddapp.project.ui.TeddButtonKind
import org.teddapp.project.ui.TeddButtonSize
import org.teddapp.project.ui.TeddCard
import org.teddapp.project.ui.TeddCheckboxRow
import org.teddapp.project.ui.TeddDateField
import org.teddapp.project.ui.TeddFineProgress
import org.teddapp.project.ui.TeddIcon
import org.teddapp.project.ui.TeddIconButton
import org.teddapp.project.ui.TeddMenu
import org.teddapp.project.ui.TeddMenuEntry
import org.teddapp.project.ui.TeddThousandsTransformation
import org.teddapp.project.ui.TeddTokens
import org.teddapp.project.ui.TeddType
import org.teddapp.project.ui.skills.DeadlineTone
import org.teddapp.project.ui.skills.SkillDeadline

/**
 * Les prêts dans le Patrimoine : ce qu'on me doit et ce que je dois. Chaque prêt ouvert
 * tient sur une carte avec son restant, sa barre de remboursement, son échéance et le
 * bouton qui enregistre un remboursement : « Être remboursé » pour une créance,
 * « Rembourser » pour une dette. Les prêts soldés se replient en bas.
 */
@Composable
internal fun LoansSection(
    loans: List<Loan>,
    payments: List<Loan_payment>,
    hideBalance: Boolean,
    compact: Boolean,
    onCreate: () -> Unit,
    onEdit: (Loan) -> Unit,
    onRepay: (Loan) -> Unit,
    onPayments: (Loan) -> Unit,
    onDelete: (Loan) -> Unit,
) {
    val live = loans.filter { it.is_deleted == 0L }
    val open = live.filter { it.status == "open" }
    val settled = live.filter { it.status != "open" }
    var showSettled by remember { mutableStateOf(false) }

    Row(verticalAlignment = Alignment.CenterVertically) {
        Text("PRÊTS", style = TeddType.Overline, color = TeddTokens.InkMuted, modifier = Modifier.weight(1f))
        TeddButton(
            text = "Nouveau prêt",
            onClick = onCreate,
            kind = TeddButtonKind.Secondary,
            size = TeddButtonSize.Sm,
            leadingIcon = { TeddIcon(name = "plus", size = 12.dp, color = TeddTokens.Ink) },
        )
    }
    if (live.isEmpty()) {
        TeddCard(modifier = Modifier.fillMaxWidth(), padding = PaddingValues(20.dp)) {
            Text("Aucun prêt", style = TeddType.Heading, color = TeddTokens.Ink)
            Text(
                text = "Ce que tu as prêté et ce qu'on t'a prêté, avec les remboursements jusqu'au solde. " +
                    "Une facture due ou un service rendu s'y notent aussi, sans qu'un compte bouge.",
                style = TeddType.BodyLg,
                color = TeddTokens.InkMuted,
                modifier = Modifier.padding(top = 8.dp),
            )
        }
        return
    }
    open.forEach { loan ->
        LoanRow(loan, payments, hideBalance, compact, onEdit = { onEdit(loan) }, onRepay = { onRepay(loan) }, onPayments = { onPayments(loan) }, onDelete = { onDelete(loan) })
    }
    if (settled.isNotEmpty()) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .clickable(interactionSource = remember { MutableInteractionSource() }, indication = null) { showSettled = !showSettled }
                .padding(vertical = 4.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(6.dp),
        ) {
            TeddIcon(name = if (showSettled) "chevron-down" else "chevron-right", size = 12.dp, color = TeddTokens.InkMuted)
            Text(
                text = if (settled.size == 1) "1 prêt soldé" else "${settled.size} prêts soldés",
                style = TeddType.Label,
                color = TeddTokens.InkMuted,
            )
        }
        if (showSettled) settled.forEach { loan ->
            LoanRow(loan, payments, hideBalance, compact, onEdit = { onEdit(loan) }, onRepay = { onRepay(loan) }, onPayments = { onPayments(loan) }, onDelete = { onDelete(loan) })
        }
    }
}

/** Libellé du sens, du point de vue de l'utilisateur. */
internal fun loanDirectionLabel(loan: Loan): String =
    if (loan.direction == LoanDirection.LENT) "On me doit" else "Je dois"

@Composable
private fun LoanRow(
    loan: Loan,
    payments: List<Loan_payment>,
    hideBalance: Boolean,
    compact: Boolean,
    onEdit: () -> Unit,
    onRepay: () -> Unit,
    onPayments: () -> Unit,
    onDelete: () -> Unit,
) {
    val lent = loan.direction == LoanDirection.LENT
    val settled = loan.status != "open"
    val due = LoanMath.due(loan)
    val remaining = LoanMath.remaining(loan, payments)
    val fraction = LoanMath.paidFraction(loan, payments)
    val accent = if (lent) TeddTokens.Success else TeddTokens.Warning
    val deadline = if (settled) null else SkillDeadline.label(loan.due_date, TODAY_ISO)
    var menu by remember { mutableStateOf(false) }

    TeddCard(modifier = Modifier.fillMaxWidth(), padding = PaddingValues(14.dp)) {
        Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(12.dp)) {
            Box(
                modifier = Modifier.size(34.dp).clip(RoundedCornerShape(TeddTokens.RSm)).background(accent.copy(alpha = 0.13f), RoundedCornerShape(TeddTokens.RSm)),
                contentAlignment = Alignment.Center,
            ) { TeddIcon(name = if (lent) "arrow-right" else "arrow-left", size = 15.dp, color = accent, strokeWidth = 2.dp) }
            Column(modifier = Modifier.weight(1f)) {
                Text(loan.counterparty, style = TeddType.Heading, color = TeddTokens.Ink, maxLines = 1)
                Text(
                    text = buildString {
                        append(loanDirectionLabel(loan))
                        append(" · ")
                        append(if (hideBalance) "••••" else fmtMoney(due, loan.currency, compact = true))
                        if (loan.interest > 0L && !hideBalance) append(" dont ${fmtMoney(loan.interest, loan.currency, compact = true)} d'intérêts")
                        append(" · ")
                        append(org.teddapp.project.ui.TeddDates.formatShort(loan.date) ?: loan.date)
                        if (loan.account_id == null) append(" · sans mouvement")
                    },
                    color = TeddTokens.InkMuted,
                    fontSize = 11.5.sp,
                    maxLines = 2,
                )
            }
            Column(horizontalAlignment = Alignment.End) {
                Text(
                    text = when {
                        hideBalance -> "••••"
                        settled -> "soldé"
                        else -> fmtMoney(remaining, loan.currency, compact = false)
                    },
                    style = TeddType.Numeral.copy(fontSize = 17.sp, lineHeight = 21.sp),
                    color = if (settled) TeddTokens.InkMuted else TeddTokens.Ink,
                )
                if (deadline != null) {
                    Text(
                        text = deadline.text,
                        style = TeddType.Mono,
                        color = if (deadline.tone == DeadlineTone.Calm) TeddTokens.InkMuted else TeddTokens.Danger,
                        fontSize = 11.sp,
                    )
                } else if (!settled) {
                    Text("restant", color = TeddTokens.InkMuted, fontSize = 11.sp)
                }
            }
            Box {
                TeddIconButton(onClick = { menu = true }, size = 30.dp) { TeddIcon(name = "more", size = 14.dp, color = TeddTokens.InkMuted) }
                TeddMenu(
                    expanded = menu,
                    onDismiss = { menu = false },
                    alignEnd = true,
                    width = 220.dp,
                    entries = listOf(
                        TeddMenuEntry("Modifier", icon = "edit", onClick = { menu = false; onEdit() }),
                        TeddMenuEntry("Les remboursements", icon = "list", onClick = { menu = false; onPayments() }),
                        TeddMenuEntry("Supprimer", icon = "trash", danger = true, onClick = { menu = false; onDelete() }),
                    ),
                )
            }
        }
        Row(
            modifier = Modifier.fillMaxWidth().padding(top = 12.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(12.dp),
        ) {
            TeddFineProgress(fraction = fraction, accent = accent, modifier = Modifier.weight(1f))
            if (!settled) {
                TeddButton(
                    text = if (lent) "Être remboursé" else "Rembourser",
                    onClick = onRepay,
                    kind = TeddButtonKind.Primary,
                    size = TeddButtonSize.Sm,
                )
            }
        }
        if (!compact && !loan.note.isNullOrBlank()) {
            Text(loan.note, color = TeddTokens.InkMuted, style = TeddType.Caption, modifier = Modifier.padding(top = 8.dp), maxLines = 2)
        }
    }
}

// ── Nouveau prêt, modification ─────────────────────────────────────────

/**
 * Création ou modification d'un prêt. À la création : le sens, la personne, le capital,
 * les intérêts (un montant, ou un taux qui le calcule), la date, l'échéance, la note, et
 * si l'argent a bougé sur un compte. Après coup, seuls la personne, les intérêts,
 * l'échéance et la note se corrigent.
 */
@Composable
internal fun LoanEditor(
    existing: Loan?,
    accounts: List<Finance_account>,
    preferredAccountId: String?,
    defaultCurrency: String,
    onClose: () -> Unit,
    onCreate: (direction: String, counterparty: String, principal: Long, interest: Long, date: String, dueDate: String?, note: String?, accountId: String?) -> Unit,
    onUpdate: (loan: Loan, counterparty: String, interest: Long, dueDate: String?, note: String?) -> Unit,
) {
    var direction by remember(existing) { mutableStateOf(existing?.direction ?: LoanDirection.LENT) }
    var counterparty by remember(existing) { mutableStateOf(existing?.counterparty ?: "") }
    var principal by remember(existing) { mutableStateOf(existing?.principal?.toString() ?: "") }
    var interest by remember(existing) { mutableStateOf(existing?.interest?.takeIf { it > 0L }?.toString() ?: "") }
    var rate by remember(existing) { mutableStateOf("") }
    var date by remember(existing) { mutableStateOf(existing?.date ?: TODAY_ISO) }
    var dueDate by remember(existing) { mutableStateOf(existing?.due_date ?: "") }
    var note by remember(existing) { mutableStateOf(existing?.note ?: "") }
    var moved by remember(existing) { mutableStateOf(existing?.account_id != null || existing == null) }
    var accountId by remember(existing) { mutableStateOf(existing?.account_id ?: preferredAccountId ?: accounts.firstOrNull()?.id) }
    val currency = existing?.currency ?: accounts.firstOrNull { it.id == accountId }?.currency ?: defaultCurrency
    val principalValue = principal.toLongOrNull() ?: 0L
    val interestValue = interest.toLongOrNull() ?: 0L
    val creating = existing == null
    val valid = counterparty.isNotBlank() && principalValue > 0L && date.isNotBlank() && (!moved || accountId != null)

    Dialog(onDismissRequest = onClose, properties = DialogProperties(usePlatformDefaultWidth = false)) {
        Column(
            modifier = Modifier
                .width(460.dp)
                .heightIn(max = 680.dp)
                .clip(RoundedCornerShape(TeddTokens.RXl))
                .background(TeddTokens.Bg, RoundedCornerShape(TeddTokens.RXl))
                .verticalScroll(rememberScrollState())
                .padding(24.dp),
            verticalArrangement = Arrangement.spacedBy(14.dp),
        ) {
            Text(if (creating) "Nouveau prêt" else "Modifier le prêt", style = TeddType.Title2, color = TeddTokens.Ink)
            if (creating) {
                Column {
                    Text("SENS", style = TeddType.Overline, color = TeddTokens.InkMuted, modifier = Modifier.padding(bottom = 6.dp))
                    Row(horizontalArrangement = Arrangement.spacedBy(6.dp)) {
                        listOf(LoanDirection.LENT to "J'ai prêté, on me doit", LoanDirection.BORROWED to "On m'a prêté, je dois").forEach { (k, label) ->
                            val on = direction == k
                            Box(
                                modifier = Modifier
                                    .weight(1f)
                                    .clip(RoundedCornerShape(TeddTokens.RSm))
                                    .background(if (on) TeddTokens.LimeSoft else TeddTokens.SurfaceSunken, RoundedCornerShape(TeddTokens.RSm))
                                    .border(1.dp, if (on) TeddTokens.LimeStrong else TeddTokens.Line, RoundedCornerShape(TeddTokens.RSm))
                                    .clickable(interactionSource = remember { MutableInteractionSource() }, indication = null) { direction = k }
                                    .padding(horizontal = 10.dp, vertical = 10.dp),
                                contentAlignment = Alignment.Center,
                            ) {
                                Text(label, color = if (on) TeddTokens.LimeStrong else TeddTokens.Ink, fontSize = 12.sp, fontWeight = FontWeight.SemiBold, maxLines = 2)
                            }
                        }
                    }
                }
            }
            LoanField("Personne", counterparty, "Moussa, le tailleur, la tontine…", KeyboardType.Text) { counterparty = it }
            if (creating) {
                LoanField("Capital ($currency)", principal, "Ce qui a été prêté ou ce qui est dû", KeyboardType.Number) { principal = it.filter { c -> c.isDigit() } }
            }
            Row(horizontalArrangement = Arrangement.spacedBy(10.dp)) {
                Box(modifier = Modifier.weight(1.4f)) {
                    LoanField("Intérêts ($currency)", interest, "0", KeyboardType.Number) { interest = it.filter { c -> c.isDigit() }; rate = "" }
                }
                Box(modifier = Modifier.weight(1f)) {
                    // Un taux calcule le montant une fois, sur le capital ; c'est le montant qui est gardé.
                    LoanField("ou taux (%)", rate, "10", KeyboardType.Number) { typed ->
                        rate = typed.filter { c -> c.isDigit() || c == '.' || c == ',' }
                        val pct = rate.replace(',', '.').toDoubleOrNull()
                        val base = (if (creating) principalValue else existing?.principal ?: 0L)
                        if (pct != null) interest = kotlin.math.round(base * pct / 100.0).toLong().toString()
                    }
                }
            }
            if (creating) {
                TeddDateField(label = "Date", value = date, onValueChange = { date = it }, placeholder = "Aujourd'hui", clearable = false)
            }
            TeddDateField(label = "Échéance", value = dueDate, onValueChange = { dueDate = it }, placeholder = "Aucune échéance")
            LoanField("Note", note, "Pour sa moto, facture n° 12…", KeyboardType.Text) { note = it }
            if (creating) {
                TeddCheckboxRow(
                    label = if (direction == LoanDirection.LENT) "L'argent est sorti d'un compte" else "L'argent est entré sur un compte",
                    checked = moved,
                    onCheckedChange = { moved = it },
                )
                if (moved) {
                    FinanceAccountChips(accounts = accounts.filter { it.is_deleted == 0L }, selectedId = accountId, onSelect = { accountId = it })
                } else {
                    Text(
                        text = "Rien ne bouge sur les comptes : une facture due, un service rendu. Les remboursements, eux, passeront par un compte.",
                        color = TeddTokens.InkMuted,
                        style = TeddType.Caption,
                    )
                }
            }
            Spacer(modifier = Modifier.height(2.dp))
            Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                Spacer(modifier = Modifier.weight(1f))
                TeddButton(text = "Annuler", onClick = onClose, kind = TeddButtonKind.Secondary)
                TeddButton(
                    text = if (creating) "Ajouter" else "Enregistrer",
                    onClick = {
                        if (existing == null) {
                            onCreate(direction, counterparty.trim(), principalValue, interestValue, date, dueDate.ifBlank { null }, note.ifBlank { null }, if (moved) accountId else null)
                        } else {
                            onUpdate(existing, counterparty.trim(), interestValue, dueDate.ifBlank { null }, note.ifBlank { null })
                        }
                    },
                    kind = TeddButtonKind.Primary,
                    enabled = valid,
                )
            }
        }
    }
}

/** Un remboursement : le montant, prérempli avec le restant, la date, le compte, une note. */
@Composable
internal fun LoanRepayDialog(
    loan: Loan,
    payments: List<Loan_payment>,
    accounts: List<Finance_account>,
    preferredAccountId: String?,
    onClose: () -> Unit,
    onSave: (amount: Long, date: String, accountId: String, note: String?) -> Unit,
) {
    val lent = loan.direction == LoanDirection.LENT
    val remaining = LoanMath.remaining(loan, payments)
    var amount by remember(loan.id) { mutableStateOf(remaining.toString()) }
    var date by remember(loan.id) { mutableStateOf(TODAY_ISO) }
    var note by remember(loan.id) { mutableStateOf("") }
    var accountId by remember(loan.id) { mutableStateOf(loan.account_id ?: preferredAccountId ?: accounts.firstOrNull()?.id) }
    val value = amount.toLongOrNull() ?: 0L
    val valid = value in 1..remaining && accountId != null && date.isNotBlank()

    Dialog(onDismissRequest = onClose, properties = DialogProperties(usePlatformDefaultWidth = false)) {
        Column(
            modifier = Modifier
                .width(440.dp)
                .clip(RoundedCornerShape(TeddTokens.RXl))
                .background(TeddTokens.Bg, RoundedCornerShape(TeddTokens.RXl))
                .verticalScroll(rememberScrollState())
                .padding(24.dp),
            verticalArrangement = Arrangement.spacedBy(14.dp),
        ) {
            Text(if (lent) "Être remboursé par ${loan.counterparty}" else "Rembourser ${loan.counterparty}", style = TeddType.Title2, color = TeddTokens.Ink)
            Text(
                text = "Restant : ${fmtMoney(remaining, loan.currency)}",
                color = TeddTokens.InkMuted,
                style = TeddType.Body,
            )
            LoanField("Montant (${loan.currency})", amount, org.teddapp.project.ui.formatThousands(remaining), KeyboardType.Number) { amount = it.filter { c -> c.isDigit() } }
            if (value > remaining) {
                Text("Le remboursement dépasse le restant.", color = TeddTokens.Danger, style = TeddType.Caption)
            }
            TeddDateField(label = "Date", value = date, onValueChange = { date = it }, placeholder = "Aujourd'hui", clearable = false)
            Column {
                Text(if (lent) "SUR LE COMPTE" else "DEPUIS LE COMPTE", style = TeddType.Overline, color = TeddTokens.InkMuted, modifier = Modifier.padding(bottom = 6.dp))
                FinanceAccountChips(accounts = accounts.filter { it.is_deleted == 0L }, selectedId = accountId, onSelect = { accountId = it })
            }
            LoanField("Note", note, "Facultative", KeyboardType.Text) { note = it }
            Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                Spacer(modifier = Modifier.weight(1f))
                TeddButton(text = "Annuler", onClick = onClose, kind = TeddButtonKind.Secondary)
                TeddButton(
                    text = "Enregistrer",
                    onClick = { accountId?.let { onSave(value, date, it, note.ifBlank { null }) } },
                    kind = TeddButtonKind.Primary,
                    enabled = valid,
                )
            }
        }
    }
}

/** Les remboursements d'un prêt, et le moyen d'en retirer un. */
@Composable
internal fun LoanPaymentsDialog(
    loan: Loan,
    payments: List<Loan_payment>,
    accounts: List<Finance_account>,
    onClose: () -> Unit,
    onDeletePayment: (Loan_payment) -> Unit,
) {
    val own = payments.filter { it.loan_id == loan.id && it.is_deleted == 0L }.sortedBy { it.date }
    val remaining = LoanMath.remaining(loan, payments)
    Dialog(onDismissRequest = onClose, properties = DialogProperties(usePlatformDefaultWidth = false)) {
        Column(
            modifier = Modifier
                .width(440.dp)
                .heightIn(max = 600.dp)
                .clip(RoundedCornerShape(TeddTokens.RXl))
                .background(TeddTokens.Bg, RoundedCornerShape(TeddTokens.RXl))
                .verticalScroll(rememberScrollState())
                .padding(24.dp),
            verticalArrangement = Arrangement.spacedBy(10.dp),
        ) {
            Text("Remboursements · ${loan.counterparty}", style = TeddType.Title2, color = TeddTokens.Ink)
            Text(
                text = "Dû ${fmtMoney(LoanMath.due(loan), loan.currency, compact = true)} · restant ${fmtMoney(remaining, loan.currency, compact = true)}",
                color = TeddTokens.InkMuted,
                style = TeddType.Body,
            )
            if (own.isEmpty()) {
                Text("Aucun remboursement pour l'instant.", color = TeddTokens.InkMuted, style = TeddType.Caption, modifier = Modifier.padding(vertical = 8.dp))
            }
            own.forEach { p ->
                val acc = accounts.firstOrNull { it.id == p.account_id }
                Row(
                    modifier = Modifier.fillMaxWidth().padding(vertical = 4.dp),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(10.dp),
                ) {
                    Column(modifier = Modifier.weight(1f)) {
                        Text(org.teddapp.project.ui.TeddDates.formatShort(p.date, withYear = true) ?: p.date, style = TeddType.Body, color = TeddTokens.Ink)
                        Text(listOfNotNull(acc?.name, p.note).joinToString(" · "), style = TeddType.Caption, color = TeddTokens.InkMuted, maxLines = 1)
                    }
                    Text(fmtMoney(p.amount, loan.currency), style = TeddType.Amount, color = TeddTokens.Ink)
                    TeddIconButton(onClick = { onDeletePayment(p) }, size = 28.dp) { TeddIcon(name = "trash", size = 13.dp, color = TeddTokens.InkMuted) }
                }
            }
            Row(modifier = Modifier.fillMaxWidth().padding(top = 6.dp), horizontalArrangement = Arrangement.End) {
                TeddButton(text = "Fermer", onClick = onClose, kind = TeddButtonKind.Secondary)
            }
        }
    }
}

@Composable
private fun LoanField(label: String, value: String, placeholder: String, keyboard: KeyboardType, onChange: (String) -> Unit) {
    Column {
        Text(label.uppercase(), style = TeddType.Overline, color = TeddTokens.InkMuted, modifier = Modifier.padding(bottom = 6.dp))
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .height(44.dp)
                .clip(RoundedCornerShape(TeddTokens.RSm))
                .background(TeddTokens.SurfaceSunken, RoundedCornerShape(TeddTokens.RSm))
                .padding(horizontal = 12.dp),
            contentAlignment = Alignment.CenterStart,
        ) {
            BasicTextField(
                value = value,
                onValueChange = onChange,
                singleLine = true,
                keyboardOptions = KeyboardOptions(keyboardType = keyboard),
                // Les montants : milliers séparés à l'affichage, la valeur reste en chiffres nus.
                visualTransformation = if (keyboard == KeyboardType.Number && value.all { it.isDigit() }) TeddThousandsTransformation else VisualTransformation.None,
                textStyle = (if (keyboard == KeyboardType.Number) TeddType.Mono else TeddType.Body).copy(color = TeddTokens.Ink, fontSize = 14.sp),
                cursorBrush = SolidColor(TeddTokens.LimeStrong),
                modifier = Modifier.fillMaxWidth(),
                decorationBox = { inner ->
                    if (value.isEmpty()) Text(placeholder, color = TeddTokens.InkSubtle, fontSize = 14.sp)
                    inner()
                },
            )
        }
    }
}

/** Les deux chiffres du Patrimoine : ce qu'on me doit, ce que je dois, sur les prêts ouverts. */
internal data class LoanTotals(val receivable: Long, val debt: Long)

internal fun loanTotals(loans: List<Loan>, payments: List<Loan_payment>, currency: String): LoanTotals {
    var receivable = 0L
    var debt = 0L
    loans.filter { it.is_deleted == 0L && it.status == "open" && it.currency == currency }.forEach { loan ->
        val r = LoanMath.remaining(loan, payments)
        if (loan.direction == LoanDirection.LENT) receivable += r else debt += r
    }
    return LoanTotals(receivable, debt)
}

@Composable
internal fun LoanTotalsCards(totals: LoanTotals, currency: String, hide: Boolean, compact: Boolean) {
    Row(horizontalArrangement = Arrangement.spacedBy(if (compact) 12.dp else 14.dp)) {
        LoanTotalCard("ON ME DOIT", totals.receivable, currency, hide, TeddTokens.Success, Modifier.weight(1f))
        LoanTotalCard("JE DOIS", totals.debt, currency, hide, TeddTokens.Warning, Modifier.weight(1f))
    }
}

@Composable
private fun LoanTotalCard(title: String, value: Long, currency: String, hide: Boolean, accent: androidx.compose.ui.graphics.Color, modifier: Modifier) {
    TeddCard(modifier = modifier.fillMaxWidth(), padding = PaddingValues(18.dp)) {
        Text(title, style = TeddType.Overline, color = TeddTokens.InkMuted)
        Text(
            text = if (hide) "••••" else fmtMoney(value, currency, compact = true),
            style = TeddType.Numeral.copy(fontSize = 26.sp, lineHeight = 32.sp),
            color = if (value == 0L || hide) TeddTokens.Ink else accent,
            modifier = Modifier.padding(top = 6.dp),
        )
        Text(
            text = if (value == 0L) "rien en cours" else "sur les prêts ouverts",
            color = TeddTokens.InkMuted,
            fontSize = 12.sp,
            modifier = Modifier.padding(top = 4.dp),
        )
    }
}
